import { useState, useEffect, useCallback } from 'react';
import {
  institutionReports,
  institutionConfig,
  institutionUsers,
  type RecordStats,
  type StaffRecordStats,
  type WorkflowStats,
  type ExportRecord,
  type DocumentType,
  type DocumentStatus,
  type SecurityClassification,
  type User,
} from '@/services/institution';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import { Input } from '@/components/common/Input';
import styles from './ReportsPage.module.css';

type ReportTab = 'overview' | 'staff' | 'workflows' | 'export';

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [loading, setLoading] = useState(true);

  // Stats data
  const [recordStats, setRecordStats] = useState<RecordStats | null>(null);
  const [staffStats, setStaffStats] = useState<StaffRecordStats | null>(null);
  const [workflowStats, setWorkflowStats] = useState<WorkflowStats | null>(null);

  // Export state
  const [exportData, setExportData] = useState<ExportRecord[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    documentTypeId: '',
    statusId: '',
    classificationId: '',
    subjectUserId: '',
    dateFrom: '',
    dateTo: '',
  });

  // Reference data for export filters
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [statuses, setStatuses] = useState<DocumentStatus[]>([]);
  const [classifications, setClassifications] = useState<SecurityClassification[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [recordsRes, staffRes, workflowRes] = await Promise.all([
        institutionReports.getRecordStats(),
        institutionReports.getStaffStats(),
        institutionReports.getWorkflowStats(),
      ]);

      if (recordsRes.success && recordsRes.data) setRecordStats(recordsRes.data);
      if (staffRes.success && staffRes.data) setStaffStats(staffRes.data);
      if (workflowRes.success && workflowRes.data) setWorkflowStats(workflowRes.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [typesRes, statusesRes, classRes, usersRes] = await Promise.all([
        institutionConfig.getDocumentTypes(),
        institutionConfig.getDocumentStatuses(),
        institutionConfig.getSecurityClassifications(),
        institutionUsers.list({ perPage: 100 }),
      ]);

      if (typesRes.success && typesRes.data) setDocumentTypes(typesRes.data);
      if (statusesRes.success && statusesRes.data) setStatuses(statusesRes.data);
      if (classRes.success && classRes.data) setClassifications(classRes.data);
      if (usersRes.success && usersRes.data) setStaffMembers(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchReferenceData();
  }, [fetchStats, fetchReferenceData]);

  async function handleExport() {
    setExporting(true);
    try {
      const params: Record<string, string | undefined> = {};
      if (exportFilters.documentTypeId) params.documentTypeId = exportFilters.documentTypeId;
      if (exportFilters.statusId) params.statusId = exportFilters.statusId;
      if (exportFilters.classificationId) params.classificationId = exportFilters.classificationId;
      if (exportFilters.subjectUserId) params.subjectUserId = exportFilters.subjectUserId;
      if (exportFilters.dateFrom) params.dateFrom = new Date(exportFilters.dateFrom).toISOString();
      if (exportFilters.dateTo) params.dateTo = new Date(exportFilters.dateTo).toISOString();

      const response = await institutionReports.exportRecords(params);
      if (response.success && response.data) {
        setExportData(response.data);
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }

  function downloadCSV() {
    if (exportData.length === 0) return;

    const headers = [
      'ID',
      'Title',
      'Reference Number',
      'Record Type',
      'Classification',
      'Status',
      'Staff Name',
      'Staff Employee ID',
      'Created By',
      'Created At',
      'Updated At',
    ];

    const rows = exportData.map((record) => [
      record.id,
      `"${record.title.replace(/"/g, '""')}"`,
      record.referenceNumber || '',
      record.recordType,
      record.classification,
      record.status,
      record.staffName || '',
      record.staffEmployeeId || '',
      record.createdBy,
      record.createdAt,
      record.updatedAt,
    ]);

    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `staff-records-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function formatNumber(num: number): string {
    return num.toLocaleString();
  }

  function getPercentage(part: number, total: number): string {
    if (total === 0) return '0%';
    return `${Math.round((part / total) * 100)}%`;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Reports & Analytics</h1>
          <p className={styles.subtitle}>View statistics and export staff records data</p>
        </div>
      </div>

      <div className={styles.tabs}>
        {(['overview', 'staff', 'workflows', 'export'] as ReportTab[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && 'Record Statistics'}
            {tab === 'staff' && 'Staff Analysis'}
            {tab === 'workflows' && 'Workflow Metrics'}
            {tab === 'export' && 'Export Data'}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {loading && activeTab !== 'export' ? (
          <div className={styles.loading}>Loading reports...</div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && recordStats && (
              <div className={styles.overviewTab}>
                <div className={styles.statsCards}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(recordStats.totalRecords)}</span>
                      <span className={styles.statLabel}>Total Records</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(recordStats.recordsCreatedToday)}</span>
                      <span className={styles.statLabel}>Created Today</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                        <polyline points="17 6 23 6 23 12" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(recordStats.recordsCreatedThisWeek)}</span>
                      <span className={styles.statLabel}>This Week</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(recordStats.recordsCreatedThisMonth)}</span>
                      <span className={styles.statLabel}>This Month</span>
                    </div>
                  </div>
                </div>

                <div className={styles.chartsRow}>
                  <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>Records by Type</h3>
                    <div className={styles.barChart}>
                      {recordStats.recordsByType.slice(0, 8).map((item) => (
                        <div key={item.typeId} className={styles.barItem}>
                          <div className={styles.barLabel}>{item.typeName}</div>
                          <div className={styles.barContainer}>
                            <div
                              className={styles.bar}
                              style={{
                                width: getPercentage(
                                  item.count,
                                  Math.max(...recordStats.recordsByType.map((t) => t.count))
                                ),
                              }}
                            />
                            <span className={styles.barValue}>{item.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>Records by Status</h3>
                    <div className={styles.barChart}>
                      {recordStats.recordsByStatus.map((item) => (
                        <div key={item.statusId} className={styles.barItem}>
                          <div className={styles.barLabel}>{item.statusName}</div>
                          <div className={styles.barContainer}>
                            <div
                              className={styles.bar}
                              style={{
                                width: getPercentage(
                                  item.count,
                                  Math.max(...recordStats.recordsByStatus.map((s) => s.count))
                                ),
                              }}
                            />
                            <span className={styles.barValue}>{item.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Records by Classification</h3>
                  <div className={styles.classificationList}>
                    {recordStats.recordsByClassification.map((item) => (
                      <div key={item.classificationId} className={styles.classificationItem}>
                        <span className={styles.classificationName}>{item.classificationName}</span>
                        <span className={styles.classificationCount}>{formatNumber(item.count)}</span>
                        <span className={styles.classificationPercent}>
                          {getPercentage(item.count, recordStats.totalRecords)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && staffStats && (
              <div className={styles.staffTab}>
                <div className={styles.statsCards}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(staffStats.totalStaffWithRecords)}</span>
                      <span className={styles.statLabel}>Staff with Records</span>
                    </div>
                  </div>
                </div>

                <div className={styles.chartsRow}>
                  <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>Records by Department</h3>
                    <div className={styles.barChart}>
                      {staffStats.staffByDepartment.slice(0, 10).map((item) => (
                        <div key={item.departmentId} className={styles.barItem}>
                          <div className={styles.barLabel}>
                            {item.departmentName}
                            <span className={styles.barSubLabel}>({item.staffCount} staff)</span>
                          </div>
                          <div className={styles.barContainer}>
                            <div
                              className={styles.bar}
                              style={{
                                width: getPercentage(
                                  item.recordCount,
                                  Math.max(...staffStats.staffByDepartment.map((d) => d.recordCount)) || 1
                                ),
                              }}
                            />
                            <span className={styles.barValue}>{item.recordCount}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.chartCard}>
                    <h3 className={styles.chartTitle}>Top Staff by Records</h3>
                    <div className={styles.staffList}>
                      {staffStats.recordsPerStaff.filter(s => s.recordCount > 0).slice(0, 10).map((item, index) => (
                        <div key={item.userId} className={styles.staffItem}>
                          <span className={styles.staffRank}>{index + 1}</span>
                          <div className={styles.staffInfo}>
                            <span className={styles.staffName}>{item.name}</span>
                            {item.employeeId && (
                              <span className={styles.staffId}>{item.employeeId}</span>
                            )}
                          </div>
                          <span className={styles.staffCount}>{item.recordCount} records</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Workflows Tab */}
            {activeTab === 'workflows' && workflowStats && (
              <div className={styles.workflowsTab}>
                <div className={styles.statsCards}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4H8V8H4V4ZM12 4H16V8H12V4ZM12 12H16V16H12V12Z" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(workflowStats.totalWorkflows)}</span>
                      <span className={styles.statLabel}>Total Workflows</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.warning}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(workflowStats.activeWorkflows)}</span>
                      <span className={styles.statLabel}>Active</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.success}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(workflowStats.completedWorkflows)}</span>
                      <span className={styles.statLabel}>Completed</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.error}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    </div>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(workflowStats.rejectedWorkflows)}</span>
                      <span className={styles.statLabel}>Rejected</span>
                    </div>
                  </div>
                </div>

                <div className={styles.statsCards}>
                  <div className={styles.statCard}>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{formatNumber(workflowStats.pendingTasks)}</span>
                      <span className={styles.statLabel}>Pending Tasks</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statContent}>
                      <span className={`${styles.statValue} ${workflowStats.overdueTasks > 0 ? styles.errorText : ''}`}>
                        {formatNumber(workflowStats.overdueTasks)}
                      </span>
                      <span className={styles.statLabel}>Overdue Tasks</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statContent}>
                      <span className={styles.statValue}>{workflowStats.avgCompletionDays}</span>
                      <span className={styles.statLabel}>Avg. Days to Complete</span>
                    </div>
                  </div>
                </div>

                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Workflows by Template</h3>
                  <div className={styles.barChart}>
                    {workflowStats.workflowsByTemplate.map((item) => (
                      <div key={item.templateId} className={styles.barItem}>
                        <div className={styles.barLabel}>{item.templateName}</div>
                        <div className={styles.barContainer}>
                          <div
                            className={styles.bar}
                            style={{
                              width: getPercentage(
                                item.count,
                                Math.max(...workflowStats.workflowsByTemplate.map((t) => t.count)) || 1
                              ),
                            }}
                          />
                          <span className={styles.barValue}>{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Export Tab */}
            {activeTab === 'export' && (
              <div className={styles.exportTab}>
                <div className={styles.exportFilters}>
                  <h3 className={styles.exportTitle}>Export Filters</h3>
                  <p className={styles.exportDescription}>
                    Select filters to export specific records, or leave all blank to export all records.
                  </p>

                  <div className={styles.filterGrid}>
                    <Select
                      label="Record Type"
                      value={exportFilters.documentTypeId}
                      onChange={(e) => setExportFilters({ ...exportFilters, documentTypeId: e.target.value })}
                    >
                      <option value="">All Types</option>
                      {documentTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>

                    <Select
                      label="Status"
                      value={exportFilters.statusId}
                      onChange={(e) => setExportFilters({ ...exportFilters, statusId: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      {statuses.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </Select>

                    <Select
                      label="Classification"
                      value={exportFilters.classificationId}
                      onChange={(e) => setExportFilters({ ...exportFilters, classificationId: e.target.value })}
                    >
                      <option value="">All Classifications</option>
                      {classifications.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.name}
                        </option>
                      ))}
                    </Select>

                    <Select
                      label="Staff Member"
                      value={exportFilters.subjectUserId}
                      onChange={(e) => setExportFilters({ ...exportFilters, subjectUserId: e.target.value })}
                    >
                      <option value="">All Staff</option>
                      {staffMembers.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.firstName} {staff.lastName}
                        </option>
                      ))}
                    </Select>

                    <Input
                      label="Date From"
                      type="date"
                      value={exportFilters.dateFrom}
                      onChange={(e) => setExportFilters({ ...exportFilters, dateFrom: e.target.value })}
                    />

                    <Input
                      label="Date To"
                      type="date"
                      value={exportFilters.dateTo}
                      onChange={(e) => setExportFilters({ ...exportFilters, dateTo: e.target.value })}
                    />
                  </div>

                  <div className={styles.exportActions}>
                    <Button onClick={handleExport} disabled={exporting}>
                      {exporting ? 'Loading...' : 'Preview Data'}
                    </Button>
                    {exportData.length > 0 && (
                      <Button variant="secondary" onClick={downloadCSV}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download CSV ({exportData.length} records)
                      </Button>
                    )}
                  </div>
                </div>

                {exportData.length > 0 && (
                  <div className={styles.exportPreview}>
                    <h3 className={styles.previewTitle}>Preview ({exportData.length} records)</h3>
                    <div className={styles.previewTable}>
                      <table>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Reference</th>
                            <th>Type</th>
                            <th>Staff</th>
                            <th>Status</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {exportData.slice(0, 20).map((record) => (
                            <tr key={record.id}>
                              <td>{record.title}</td>
                              <td>{record.referenceNumber || '-'}</td>
                              <td>{record.recordType}</td>
                              <td>{record.staffName || '-'}</td>
                              <td>{record.status}</td>
                              <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {exportData.length > 20 && (
                        <p className={styles.previewMore}>
                          Showing 20 of {exportData.length} records. Download CSV for full data.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
